'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, HandHelping, Loader2, Search } from 'lucide-react';
import { BusinessCategoryPicker } from '@/components/common/business-category-picker';
import { AccountHeader } from '@/components/layouts/AccountHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import {
  SegmentedTabs,
  SegmentedTabsList,
  SegmentedTabsTrigger,
} from '@/components/ui/segmented-tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useCommunity } from '@/hooks/use-community';
import { useDiscardChangesConfirm } from '@/hooks/use-discard-changes-confirm';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { useUser } from '@/hooks/use-user';
import { communityIdFromPathname } from '@/lib/community-route';
import { giveAskApi, type GiveAskType } from '@/lib/give-ask-api';
import { cn } from '@/lib/utils';
import type { UserWithCommunities } from '@/types';

function errorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
}

function Field({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-sm font-medium">
        {label}
      </Label>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function CreateGiveAskShell({
  communitySegment,
  user,
  children,
}: {
  communitySegment?: string;
  user?: UserWithCommunities | null;
  children: ReactNode;
}) {
  if (!communitySegment) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {user ? <AccountHeader user={user} /> : null}
        <main className="flex-1 overflow-y-auto w-full px-2 py-2 md:px-6 md:py-8">
          <div className="w-full max-w-[1600px] mx-auto">{children}</div>
        </main>
      </div>
    );
  }

  return <div className="w-full min-w-0 max-w-none -m-2 md:m-0">{children}</div>;
}

export default function CreateGiveAskPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ communityId?: string }>();
  const { user } = useUser();

  const communitySegment = params.communityId ?? communityIdFromPathname(location.pathname);
  const { community: communityPkg } = useCommunity(communitySegment);

  const returnTo = communitySegment
    ? `/${communitySegment}/profile?tab=gives-asks`
    : '/profile?tab=gives-asks';

  const communitiesWithGivesAsks = useMemo(
    () => (user?.communities ?? []).filter((c) => Boolean(c.isGivesAsksEnabled)),
    [user?.communities]
  );

  const [type, setType] = useState<GiveAskType>('GIVE');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [category, setCategory] = useState<{ businessCategory: string; subCategory: string } | null>(
    null
  );
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [selectedCommunityIds, setSelectedCommunityIds] = useState<string[]>([]);
  const [communitiesReady, setCommunitiesReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allowLeave, setAllowLeave] = useState(false);

  useEffect(() => {
    if (!user || communitiesReady) return;
    setSelectedCommunityIds(communitiesWithGivesAsks.map((c) => c.id));
    setCommunitiesReady(true);
  }, [user, communitiesWithGivesAsks, communitiesReady]);

  const defaultCommunityIds = useMemo(
    () => communitiesWithGivesAsks.map((c) => c.id),
    [communitiesWithGivesAsks]
  );

  const isDirty = useMemo(() => {
    if (
      type !== 'GIVE' ||
      title.trim() ||
      description.trim() ||
      businessName.trim() ||
      category ||
      contactName.trim() ||
      contactPhone.trim() ||
      contactEmail.trim()
    ) {
      return true;
    }
    if (!communitiesReady) return false;
    if (selectedCommunityIds.length !== defaultCommunityIds.length) return true;
    const selected = new Set(selectedCommunityIds);
    return defaultCommunityIds.some((id) => !selected.has(id));
  }, [
    type,
    title,
    description,
    businessName,
    category,
    contactName,
    contactPhone,
    contactEmail,
    communitiesReady,
    selectedCommunityIds,
    defaultCommunityIds,
  ]);

  const allSelected =
    communitiesWithGivesAsks.length > 0 &&
    selectedCommunityIds.length === communitiesWithGivesAsks.length;
  const selectedCount = selectedCommunityIds.length;

  const goBack = () => navigate(returnTo);

  const toggleCommunity = (id: string, checked: boolean) => {
    setSelectedCommunityIds((prev) =>
      checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((c) => c !== id)
    );
  };

  const selectAllCommunities = () => {
    setSelectedCommunityIds(communitiesWithGivesAsks.map((c) => c.id));
  };

  const clearCommunities = () => {
    setSelectedCommunityIds([]);
  };

  const save = async () => {
    if (type === 'ASK' && title.trim().length < 3) {
      toast.error('Add what you are looking for');
      throw new Error('validation');
    }
    if (type === 'GIVE' && !contactName.trim() && !businessName.trim()) {
      toast.error("Add the person's name or their business name");
      throw new Error('validation');
    }
    if (description.trim().length < 3) {
      toast.error('Add a short description');
      throw new Error('validation');
    }
    if (selectedCommunityIds.length === 0) {
      toast.error('Turn on at least one community');
      throw new Error('validation');
    }

    setSaving(true);
    try {
      await giveAskApi.createOwn({
        type,
        title: type === 'ASK' ? title.trim() : null,
        description: description.trim(),
        businessName: businessName.trim() || null,
        category: category?.businessCategory ?? null,
        subCategory: category?.subCategory ?? null,
        contactName: contactName.trim() || null,
        contactPhone: contactPhone.trim() || null,
        contactEmail: contactEmail.trim() || null,
        communityIds: selectedCommunityIds,
      });
      toast.success(type === 'ASK' ? 'Ask added' : 'Give added');
      setAllowLeave(true);
      navigate(returnTo, { replace: true });
    } catch (err) {
      if (err instanceof Error && err.message === 'validation') throw err;
      toast.error(errorMessage(err, 'Could not save'));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const { requestDiscard, dialog: discardDialog } = useDiscardChangesConfirm({
    when: isDirty && !allowLeave,
    onSave: save,
    description: 'You have unsaved changes. Do you want to save before leaving?',
  });
  const { dialog: navGuardDialog } = useUnsavedChangesGuard({
    when: isDirty && !saving && !allowLeave,
    description: 'You have unsaved changes. If you leave now, your progress will be lost.',
  });

  const requestLeave = () => requestDiscard(goBack);

  return (
    <CreateGiveAskShell
      communitySegment={communitySegment}
      user={user as UserWithCommunities | null | undefined}
    >
      {discardDialog}
      {navGuardDialog}
      <Button
        variant="ghost"
        className="mb-2 pl-0 hover:bg-transparent hover:text-primary"
        onClick={requestLeave}
      >
        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Profile
      </Button>

      <Card className="w-full border-0 md:border md:border-border bg-background/50 backdrop-blur-md shadow-none md:shadow-2xl">
        <CardHeader className="space-y-3 p-3 md:p-6">
          <CardTitle className="text-2xl">
            {type === 'GIVE' ? 'Add a Give' : 'Add an Ask'}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {type === 'GIVE'
              ? 'Share a connection you can introduce — someone others may want to meet.'
              : 'Share what you need — a referral, vendor, hire, or introduction.'}
          </p>
          <SegmentedTabs
            value={type}
            onValueChange={(value) => setType(value as GiveAskType)}
            className="w-full"
          >
            <SegmentedTabsList variant="primary" className="mt-2 grid w-full max-w-md grid-cols-2 md:mt-4">
              <SegmentedTabsTrigger variant="primary" value="GIVE" className="gap-2">
                <HandHelping className="h-4 w-4" />
                I can offer
              </SegmentedTabsTrigger>
              <SegmentedTabsTrigger variant="primary" value="ASK" className="gap-2">
                <Search className="h-4 w-4" />
                I need
              </SegmentedTabsTrigger>
            </SegmentedTabsList>
          </SegmentedTabs>
        </CardHeader>

        <CardContent className="space-y-6 p-3 pt-0 md:space-y-8 md:p-6 md:pt-0">
          <section className="space-y-4">
            <div>
              <h2 className="text-sm font-semibold">Details</h2>
              <p className="text-xs text-muted-foreground">What should people see?</p>
            </div>

            {type === 'ASK' ? (
              <Field label="What are you looking for?" htmlFor="give-ask-title">
                <Input
                  id="give-ask-title"
                  placeholder="e.g. Interior designer in Pune"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>
            ) : null}

            <Field
              label="Description"
              hint="A short note helps people decide whether to connect."
              htmlFor="give-ask-description"
            >
              <Textarea
                id="give-ask-description"
                placeholder={
                  type === 'GIVE'
                    ? 'What you can introduce or offer…'
                    : 'What you need and any useful details…'
                }
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={2000}
                className="min-h-28"
              />
            </Field>

            <Field label="Business name (optional)" htmlFor="give-ask-business">
              <Input
                id="give-ask-business"
                placeholder="Company or practice name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
            </Field>

            <Field label="Category (optional)">
              <BusinessCategoryPicker
                businessCategory={category?.businessCategory}
                subCategory={category?.subCategory}
                onSelect={setCategory}
              />
            </Field>
          </section>

          <section className="space-y-4">
            <div>
              <h2 className="text-sm font-semibold">Connection contact</h2>
              <p className="text-xs text-muted-foreground">
                {type === 'ASK'
                  ? 'If you have someone\u2019s details and want to make a genuine introduction, add their contact here — not yours.'
                  : 'Contact for the person or company this give is about.'}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Not shown publicly. Shared only when you explicitly share it with someone.
              </p>
            </div>

            <Field label="Name" htmlFor="give-ask-contact-name">
              <Input
                id="give-ask-contact-name"
                placeholder={
                  type === 'ASK' ? 'Person to introduce' : 'Person or company contact name'
                }
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Phone (optional)" htmlFor="give-ask-phone">
                <PhoneInput
                  id="give-ask-phone"
                  value={contactPhone || undefined}
                  onChange={(v) => setContactPhone(v ?? '')}
                  placeholder="Phone number"
                  defaultCountry="IN"
                />
              </Field>
              <Field label="Email (optional)" htmlFor="give-ask-email">
                <Input
                  id="give-ask-email"
                  type="email"
                  placeholder="email@example.com"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </Field>
            </div>
          </section>

          <section className="space-y-3 rounded-2xl border bg-white p-3 md:space-y-4 md:p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold">Show in communities</h2>
                <p className="text-xs text-muted-foreground">
                  {selectedCount === 0
                    ? 'Turn on at least one community before saving.'
                    : `Will appear in ${selectedCount} community${selectedCount === 1 ? '' : 'ies'}.`}
                </p>
              </div>
              {communitiesWithGivesAsks.length > 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8"
                  onClick={allSelected ? clearCommunities : selectAllCommunities}
                >
                  {allSelected ? 'Clear all' : 'Select all'}
                </Button>
              ) : null}
            </div>

            {communitiesWithGivesAsks.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                None of your communities have Gives & Asks enabled yet.
              </p>
            ) : (
              <ul className="space-y-2">
                {communitiesWithGivesAsks.map((community) => {
                  const checked = selectedCommunityIds.includes(community.id);
                  const isCurrent =
                    community.id === communityPkg?.community?.id ||
                    community.id === communitySegment;
                  return (
                    <li
                      key={community.id}
                      className={cn(
                        'flex items-center justify-between gap-3 rounded-xl border bg-white px-3 py-3 shadow-md',
                        checked
                          ? 'border-primary/30 shadow-primary/20'
                          : 'border-border shadow-black/10'
                      )}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{community.name}</p>
                        {isCurrent ? (
                          <p className="text-xs text-muted-foreground">You are viewing this one</p>
                        ) : null}
                      </div>
                      <Switch
                        checked={checked}
                        onCheckedChange={(value) => toggleCommunity(community.id, value)}
                        aria-label={`Show in ${community.name}`}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={requestLeave} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                void save().catch(() => undefined);
              }}
              disabled={saving || selectedCommunityIds.length === 0}
              className="min-w-40"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : type === 'GIVE' ? (
                'Save give'
              ) : (
                'Save ask'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </CreateGiveAskShell>
  );
}
